import React, {useCallback, useEffect, useId, useMemo, useRef, useState} from 'react';
import {Common, NumBool} from '@taopix/taopix-core';
import {
  Button,
  PopOut,
  Horizontal,
  Vertical,
  TrashIcon,
  Dialog,
  DialogContent,
  DialogFooter,
  ThemeName,
} from '@taopix/taopix-design-system';
import {ButtonProps} from '@taopix/taopix-design-system/dist/types/Components/Button/Button';
import {PopOutProps} from '@taopix/taopix-design-system/dist/types/Components/PopOut/PopOut';
import {useTranslation} from 'react-i18next';
import {useSelector} from 'react-redux';
import {LayoutSelector, PageCountSelector, TotalPageCountSelector} from '../../../Selectors';
import {useTaopix} from '../../Context/TaopixContext';
import {getScrollParent} from '../../../Utils/Common';
import {useUiState} from '../../Context/UserInterface/UserInterfaceContext';
import {onPageCountChanged} from '../../../Utils/Page';
import {UserInterfaceActions} from '../../Context/UserInterface/UserInterfaceActionTypes';
import {useUserFeedback} from '../../Context/UserFeedback/UserFeedbackContext';
import {UserFeedbackActions} from '../../Context/UserFeedback/UserFeedbackActionTypes';

type DeletePageButtonProps = {
  pageNumber: number;
  buttonProps?: Partial<ButtonProps>;
  popoutAnchorId?: string;
  popoutProps?: Partial<PopOutProps>;
  scrollIntoView?: boolean;
  showLabel?: boolean;
  pageType?: 'page' | 'print';
  disabled?: boolean;
};

const DeletePageButton = ({
  pageNumber,
  buttonProps,
  popoutAnchorId,
  scrollIntoView,
  showLabel = true,
  pageType = 'page',
  disabled,
  ...props
}: DeletePageButtonProps) => {
  const [deletePagePopOutOpen, setDeletePagePopOutOpen] = useState(false);
  const [deletePageConfirmationOpen, setDeletePageConfirmationOpen] = useState(false);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const id = useId();

  const {t} = useTranslation(['*', 'dialogs']);
  const app = useTaopix();
  const ui = useUiState();
  const userFeedback = useUserFeedback();

  const productLayout = useSelector(LayoutSelector);
  const totalPageCount = useSelector(TotalPageCountSelector);
  const normalPageCount = useSelector(PageCountSelector);

  let pageNumberToDelete = pageNumber;

  if (
    NumBool.True === productLayout.spreads &&
    NumBool.True === productLayout.pageinsertcount &&
    pageNumber + 1 === productLayout.lastpage
  ) {
    pageNumberToDelete++;
  }

  useEffect(() => {
    const scrollElement = getScrollParent(buttonRef?.current);
    // Add a scroll listener to the scrollParent to close the popout when it is scrolled.
    if (deletePagePopOutOpen) {
      // Add the listener after a delay to avoid being triggered by the scrollIntoView method used when opening
      setTimeout(() => scrollElement?.addEventListener('scroll', closePopout), 1000);
    }

    return () => {
      // Remove the handler
      scrollElement?.removeEventListener('scroll', closePopout);
    };
  }, [deletePagePopOutOpen]);

  /**
   * Shows the popout.
   */
  const showPopout = useCallback(() => {
    // If scrollIntoView is set, scroll the requested container to put this button in the centre
    if (scrollIntoView && buttonRef.current) {
      buttonRef.current.scrollIntoView({behavior: 'smooth', block: 'center', inline: 'center'});
    }
    setDeletePagePopOutOpen(true);
  }, [scrollIntoView]);

  /**
   * Closes the popup.
   */
  const closePopout = () => {
    // Remove the scroll handler from the scrollParent
    const scrollElement = getScrollParent(buttonRef?.current);
    scrollElement?.removeEventListener('scroll', closePopout);
    // Close the popout
    setDeletePagePopOutOpen(false);
  };

  const isLastPage = (() => {
    if (NumBool.True === productLayout.spreads) {
      return pageNumber === productLayout.lastpage || pageNumber + 1 === productLayout.lastpage;
    }

    return pageNumber === productLayout.lastpage;
  })();

  const alertMessage = useMemo(() => {
    const min = productLayout.minpages;
    const pageCount = productLayout.retroprints ? normalPageCount : totalPageCount;

    switch (true) {
      case Common.isPageCover(pageNumber): {
        return t('str_WarningNotDeleteOnCover');
      }
      case !isLastPage && pageCount - (1 === productLayout.spreads ? 2 : 1) < min:
      case isLastPage && pageCount - productLayout.pageinsertcount < min: {
        return t(`${'page' === pageType ? '*' : 'retroprint'}:str_WarningNotDeletePage`);
      }
      default: {
        return '';
      }
    }
  }, [pageNumber, pageType, totalPageCount, normalPageCount]);

  const confirmationMessage = useMemo(() => {
    const deleteCurrentPageMessage = t(
      `${'page' === pageType ? '*' : 'retroprint'}:str_MessageConfirmationDeletePage1`
    );

    if (NumBool.True === productLayout.spreads) {
      // On the last page, remove the right page if insert count is 1.
      if (1 === productLayout.pageinsertcount && pageNumber + 1 === productLayout.lastpage) {
        return t('str_MessageConfirmationDeletePage2');
      }

      // Deleting the first page.
      if (0 === pageNumber && 1 === productLayout.firstpagenumber) {
        return t('str_MessageConfirmationDeleteSpread3');
      }

      // Deleting the last page.
      if (productLayout.lastpage === pageNumber) {
        if (productLayout.pageinsertcount > 1) {
          return t('str_MessageConfirmationDeleteSpread2');
        }

        return deleteCurrentPageMessage;
      }

      return t('str_MessageConfirmationDeleteSpread1');
    } else {
      return deleteCurrentPageMessage;
    }
  }, [pageNumber, totalPageCount, pageType]);

  const deletePageButtonId = `deletePageButton-${pageNumber}${id.replaceAll(':', '')}`;
  const deletePagePopOutId = 'deletePagesPopout-' + pageNumber;

  const popoutProps: Partial<PopOutProps> = {
    id: deletePagePopOutId,
    theme: 'tpx-theme-main',
    themeTip: 'Base',
    contentTheme: '' !== alertMessage ? ThemeName.Warning : ThemeName.Popouts,
    arrow: true,
    anchorId: popoutAnchorId ? popoutAnchorId : deletePageButtonId,
    className: 'flex flex-col p-md space-y-sm max-w-[300rem]',
    anchorOrigin: {vertical: Vertical.Top, horizontal: Horizontal.Centre},
    transformOrigin: {vertical: Vertical.Bottom, horizontal: Horizontal.Centre},
    popDirection: 'up',
    offset: {x: 0, y: -10},
    repositionOnResize: true,
    onClose: () => closePopout(),
    excludeIdFromClickOutside: [deletePageButtonId],
    focusFirstElement: false,
    edgeBehaviour: 'move',
    ...props.popoutProps,
  };

  return (
    <>
      <Button
        id={deletePageButtonId}
        ref={buttonRef}
        label={t('*:str_LabelPageDelete')}
        hideLabel={!showLabel}
        startIcon={<TrashIcon />}
        {...buttonProps}
        onClick={() => {
          if ('' !== alertMessage) {
            if (deletePagePopOutOpen) {
              closePopout();
            } else {
              showPopout();
            }
          } else {
            if (deletePagePopOutOpen) {
              closePopout();
            }

            setDeletePageConfirmationOpen(true);
          }
        }}
        labelLines={1}
        disabled={disabled}
        aria-pressed={deletePageConfirmationOpen}
        aria-haspopup={'dialog'}
      />
      <PopOut id={'deletePagePopout-' + pageNumber} open={deletePagePopOutOpen} {...popoutProps}>
        {alertMessage}
      </PopOut>
      <Dialog
        id={'deletePageConfirmationDialog'}
        open={deletePageConfirmationOpen}
        theme={'tpx-theme-dialogs'}
        themeTip="Dialogs"
        title={t('dialogs:str_TitleDeletePageConfirmation', {
          defaultValue: 'Delete Page Confirmation',
        })}
        showTitle={false}
      >
        <DialogContent>{confirmationMessage}</DialogContent>
        <DialogFooter isSmallScreen={ui.state.isSmallScreen}>
          <Button
            label={t('str_ButtonCancel')}
            buttonStyle="tertiary"
            onClick={() => {
              setDeletePageConfirmationOpen(false);
            }}
            className={'flex'}
          />
          <Button
            label={t('str_ButtonDelete')}
            onClick={() => {
              setDeletePageConfirmationOpen(false);

              app.core
                .get('stateManager')
                .call('deletepages', {
                  pageNumbers: [pageNumberToDelete],
                  currentPage: ui.state.pageNumber || 0,
                })
                .then(response => {
                  onPageCountChanged(
                    {spineResized: (response as any).spineResized || false},
                    ui,
                    productLayout,
                    totalPageCount
                  );
                })
                .catch(error => {
                  let message = app.core.get('server').getErrorFromResponse(error);
                  if ('' === message || !message) {
                    message = error;
                  }
                  userFeedback.dispatch(
                    UserFeedbackActions.setErrorUserFeedback({
                      title: '',
                      message,
                      showHeader: true,
                    })
                  );
                })
                .finally(() => {
                  // Close the page actions palette if it is open
                  if (ui.state.palette.section === 'pageActionsPalette') {
                    ui.dispatch(UserInterfaceActions.setToolPaletteSection(null));
                  }
                });
            }}
          />
        </DialogFooter>
      </Dialog>
    </>
  );
};

export default DeletePageButton;
